import { useCallback, useEffect, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { getDashboardTiles } from '@/features/dashboard-tiles/services/dashboardTilesService'
import type { DashboardTile } from '@/features/dashboard-tiles/types'
import { asStringIdArray } from '@/features/roles/utils/asStringIdArray'
import { isGdprConsentRequiredError } from '@/features/document-management/constants/gdprConsent'
import { resolveTileUrl } from '../utils/dashboardTileNavigation'

/**
 * Home dashboard tiles — mirrors Angular `DashboardTileReportComponent.loadTiles`:
 *   GET /api/dashboard_tiles?orderBy=tile_order&sortBy=asc, then keep only the tiles
 *   assigned to the current role (`current_role.tiles`), substituting the
 *   COMPANY_ID / PROPERTY_ID placeholders in each `tile_url`.
 */
export function useDashboardTiles() {
  const user = useAppSelector(selectUser)
  const role = user?.current_role
  const companyId = role?.company_id ?? null
  const propertyId = role?.property_id ?? null
  // Join to a primitive so the effect re-runs only when the assigned set actually changes.
  const selectedTilesKey = asStringIdArray(role?.tiles).join(',')

  const [tiles, setTiles] = useState<DashboardTile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Bumped after GDPR accept so tiles refetch once the 307 gate is cleared. */
  const [reloadToken, setReloadToken] = useState(0)

  const reload = useCallback(() => {
    setReloadToken((n) => n + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      // Defer past the synchronous effect body so state updates never cascade renders.
      await Promise.resolve()
      if (cancelled) return

      const allowed = selectedTilesKey ? selectedTilesKey.split(',') : []
      if (allowed.length === 0) {
        setTiles([])
        setError(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      try {
        const all = await getDashboardTiles({ orderBy: 'tile_order', sortBy: 'asc' })
        if (cancelled) return
        const allowedSet = new Set(allowed)
        const filtered = all
          .filter((t) => t.tile_id != null && allowedSet.has(String(t.tile_id)))
          .map((t) => ({
            ...t,
            tile_url: resolveTileUrl(t.tile_url, companyId, propertyId),
          }))
        setTiles(filtered)
      } catch (e: unknown) {
        if (cancelled) return
        // 307 GDPR gate opens the consent modal; do not show a tiles error banner.
        if (isGdprConsentRequiredError(e)) {
          setError(null)
          setTiles([])
          return
        }
        setError(e instanceof Error ? e.message : 'Failed to load dashboard tiles')
        setTiles([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [selectedTilesKey, companyId, propertyId, reloadToken])

  return { tiles, loading, error, reload }
}
